import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AppLayout from './components/AppLayout';
import HomePage from './pages/HomePage';
import PriceComparisonPage from './pages/PriceComparisonPage';
import MappingPage from './pages/MappingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminPanel from './components/AdminPanel';
import CompetitorsPage from './pages/CompetitorsPage';
import CompetitorSourcePage from './pages/CompetitorSourcePage';
import OwnProductsPage from './pages/OwnProductsPage';
import PriceDynamicsPage from './pages/PriceDynamicsPage';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Routes with the app shell: sidebar on desktop, tabs on phones */}
            <Route path="/*" element={<AppWithLayout />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Authenticated routes inside the app shell
function AppWithLayout() {
  return (
    <AppLayout>
      <Routes>
        {/* Dashboard - accessible to all authenticated users */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requireApproval={false}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Pending approval page */}
        <Route
          path="/pending-approval"
          element={
            <ProtectedRoute requireApproval={false}>
              <PendingApprovalPage />
            </ProtectedRoute>
          }
        />

        {/* Home page (scraper) - accessible to all approved users including guests */}
        <Route
          path="/"
          element={
            <ProtectedRoute
              requiredRoles={['guest', 'user', 'admin']}
              requireApproval={true}
            >
              <HomePage />
            </ProtectedRoute>
          }
        />

        {/* /database и /ukrshina заменены единой /competitors/:source */}
        <Route path="/database" element={<Navigate to="/competitors/infoshina" replace />} />
        <Route path="/ukrshina" element={<Navigate to="/competitors/ukrshina" replace />} />

        {/* Admin panel - requires admin role */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute
              requiredRoles={['admin']}
              requireApproval={true}
            >
              <AdminPanel />
            </ProtectedRoute>
          }
        />

        {/* Price comparison - approved users */}
        <Route
          path="/comparison"
          element={
            <ProtectedRoute
              requiredRoles={['user', 'admin']}
              requireApproval={true}
            >
              <PriceComparisonPage />
            </ProtectedRoute>
          }
        />

        {/* ID Mapping - approved users */}
        <Route
          path="/mapping"
          element={
            <ProtectedRoute
              requiredRoles={['user', 'admin']}
              requireApproval={true}
            >
              <MappingPage />
            </ProtectedRoute>
          }
        />

        {/* Свій каталог: товари, маржа, історія «ми проти ринку» */}
        <Route
          path="/products"
          element={
            <ProtectedRoute
              requiredRoles={['user', 'admin']}
              requireApproval={true}
            >
              <OwnProductsPage />
            </ProtectedRoute>
          }
        />

        {/* Динаміка цін конкурента між датами + вивантаження в Excel */}
        <Route
          path="/price-dynamics"
          element={
            <ProtectedRoute
              requiredRoles={['user', 'admin']}
              requireApproval={true}
            >
              <PriceDynamicsPage />
            </ProtectedRoute>
          }
        />

        {/* Competitors (parsers) - approved users */}
        <Route
          path="/competitors"
          element={
            <ProtectedRoute
              requiredRoles={['user', 'admin']}
              requireApproval={true}
            >
              <CompetitorsPage />
            </ProtectedRoute>
          }
        />

        {/* Единая страница конкретного конкурента (unified offers layer) */}
        <Route
          path="/competitors/:source"
          element={
            <ProtectedRoute
              requiredRoles={['user', 'admin']}
              requireApproval={true}
            >
              <CompetitorSourcePage />
            </ProtectedRoute>
          }
        />

        {/* Redirect root to appropriate page */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppLayout>
  );
}

export default App;
